import type { TechnicalConfigFieldKind } from "features/shared/lib/technical-config-tab/fieldKindStrategies";

import { FilterTypes } from "enums/components";
import {
  type ActiveField,
  type CatalogColumnFieldOf,
  type ConfigApiValueForKind,
  type DefinitionByKey as DefinitionByKeyFromFields,
  filterActiveFields,
  filterCatalogColumnFields,
  filterHeaderFields,
  filterTabFields,
  type FormStateFromFieldKeys,
  type FormValueForKind,
  type HeaderFieldOf,
  type TabFieldOf,
  type TechnicalConfigFieldDefinitionCore,
} from "features/shared/lib/technical-config-tab/fieldDefinitions";

import { CONTROL_LOGIC_SHADING_DEMAND, CONTROL_LOGIC_WIND_RISK, SHADING_LOCATIONS } from "./windowConfigOptions";

export const WINDOW_CONFIG_TABS = {
  characteristics: "characteristics",
  control: "control",
  shading: "shading",
} as const;

export type WindowConfigFieldDefinition = TechnicalConfigFieldDefinitionCore<WindowConfigFieldKind, WindowConfigTab> & {
  catalogColumn?: boolean;
};

export type WindowConfigFieldKind = Extract<TechnicalConfigFieldKind, "boolean" | "enum" | "number" | "string">;

type WindowConfigTab = (typeof WINDOW_CONFIG_TABS)[keyof typeof WINDOW_CONFIG_TABS];

const WINDOW_CONFIG_FIELD_DEFINITIONS = [
  /** HEADER */
  {
    default: "",
    filterType: FilterTypes.AUTOCOMPLETE_SINGLE,
    inputType: "string",
    key: "windowTypeName",
    kind: "string",
  },
  {
    default: "",
    filterType: FilterTypes.AUTOCOMPLETE_SINGLE,
    inputType: "string",
    key: "frameType",
    kind: "string",
  },
  /** CHARACTERISTICS TAB */
  {
    default: false,
    filterType: FilterTypes.SWITCH,
    isFieldInactive: true,
    key: "manuallyOverridden",
    kind: "boolean",
    tab: "characteristics",
  },
  {
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    inputType: "string",
    isFieldInactive: true,
    key: "windowTypeDbId",
    kind: "number",
    tab: "characteristics",
  },
  {
    catalogColumn: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "uValue",
    kind: "number",
    tab: "characteristics",
  },
  {
    catalogColumn: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "gValueWindow",
    kind: "number",
    tab: "characteristics",
  },
  {
    catalogColumn: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "gValueDirectWindow",
    kind: "number",
    tab: "characteristics",
  },
  {
    catalogColumn: true,
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "gValueDiffuseWindow",
    kind: "number",
    tab: "characteristics",
  },
  {
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "windowInfiltration",
    kind: "number",
    tab: "characteristics",
  },
  {
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "installationYear",
    kind: "number",
    tab: "characteristics",
  },
  {
    colSpan: 2,
    default: true,
    filterType: FilterTypes.SWITCH,
    key: "canBeOpened",
    kind: "boolean",
    tab: "characteristics",
  },
  /** SHADING TAB */
  {
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    inputType: "string",
    isFieldInactive: true,
    key: "shadingSystemDbId",
    kind: "number",
    tab: "shading",
  },
  {
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "shadingLocation",
    kind: "enum",
    options: SHADING_LOCATIONS,
    tab: "shading",
  },
  {
    colSpan: 2,
    default: "none",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "shadingSystemType",
    kind: "enum",
    tab: "shading",
  },
  {
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "gValueDirectWindowShading",
    kind: "number",
    tab: "shading",
  },
  {
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "gValueDiffuseWindowShading",
    kind: "number",
    tab: "shading",
  },
  {
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "maxWindAverage",
    kind: "number",
    tab: "shading",
  },
  {
    default: "",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "maxWindExtreme",
    kind: "number",
    tab: "shading",
  },
  /** CONTROL TAB */
  {
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "controlLogicShadingDemand",
    kind: "enum",
    options: CONTROL_LOGIC_SHADING_DEMAND,
    tab: "control",
  },
  {
    default: "",
    filterType: FilterTypes.DROPDOWN_SINGLE,
    key: "controlLogicWindRisk",
    kind: "enum",
    options: CONTROL_LOGIC_WIND_RISK,
    tab: "control",
  },
  {
    default: "0",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "cdirThreshold",
    kind: "number",
    tab: "control",
  },
  {
    default: "120",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "dirRadThreshold",
    kind: "number",
    tab: "control",
  },
  {
    default: "0.2",
    filterType: FilterTypes.INPUT,
    inputType: "number",
    key: "sfThreshold",
    kind: "number",
    tab: "control",
  },
  {
    colSpan: 2,
    default: false,
    filterType: FilterTypes.SWITCH,
    key: "thermoautomatic",
    kind: "boolean",
    tab: "control",
  },
] as const satisfies readonly WindowConfigFieldDefinition[];

export type ActiveFieldDefinition = ActiveField<(typeof WINDOW_CONFIG_FIELD_DEFINITIONS)[number]>;

export type CatalogColumnFieldDefinition = CatalogColumnFieldOf<ActiveFieldDefinition> &
  Pick<WindowConfigFieldDefinition, "labelKey">;

export type DefinitionByKey<K extends WindowConfigFormFieldKey> = DefinitionByKeyFromFields<
  typeof WINDOW_CONFIG_FIELD_DEFINITIONS,
  K
>;

export type { FormValueForKind };

export type HeaderFieldDefinition = HeaderFieldOf<ActiveFieldDefinition> &
  Pick<WindowConfigFieldDefinition, "colSpan" | "inputType" | "labelKey">;

export type TabFieldDefinition = Pick<WindowConfigFieldDefinition, "colSpan" | "inputType" | "labelKey" | "options"> &
  TabFieldOf<ActiveFieldDefinition, WindowConfigTab>;

export type WindowConfigFormFieldKey = ActiveFieldDefinition["key"];

export type WindowConfigFormState = FormStateFromFieldKeys<
  typeof WINDOW_CONFIG_FIELD_DEFINITIONS,
  WindowConfigFormFieldKey
>;

export type WindowTechnologyFields = Omit<
  {
    [K in WindowConfigFormFieldKey]: ConfigApiValueForKind<DefinitionByKey<K>["kind"]> | undefined;
  },
  "shadingLocation"
> & {
  shadingLocation?: null | string;
};

export const getActiveFieldDefinitions = (): readonly ActiveFieldDefinition[] =>
  filterActiveFields(WINDOW_CONFIG_FIELD_DEFINITIONS);

export const getHeaderFieldDefinitions = (): readonly HeaderFieldDefinition[] =>
  filterHeaderFields(getActiveFieldDefinitions());

export const getTabFieldDefinitions = (): readonly TabFieldDefinition[] => filterTabFields(getActiveFieldDefinitions());

export const getCatalogColumnDefinitions = (): readonly CatalogColumnFieldDefinition[] =>
  filterCatalogColumnFields(getActiveFieldDefinitions());

export const getWindowCatalogColumnDefinitions = (): readonly CatalogColumnFieldDefinition[] =>
  getCatalogColumnDefinitions().filter((field) =>
    ["gValueDiffuseWindow", "gValueDirectWindow", "gValueWindow", "uValue"].includes(field.key)
  );

export const getWindowConfigTabs = (): WindowConfigTab[] => [
  WINDOW_CONFIG_TABS.characteristics,
  WINDOW_CONFIG_TABS.shading,
  WINDOW_CONFIG_TABS.control,
];

export const WINDOW_CATALOG_POOL_TABS = {
  shadingTypes: "shadingTypes",
  windowTypes: "windowTypes",
} as const;

export type WindowCatalogPoolTab = (typeof WINDOW_CATALOG_POOL_TABS)[keyof typeof WINDOW_CATALOG_POOL_TABS];

export const getWindowCatalogPoolTabs = (): WindowCatalogPoolTab[] => Object.values(WINDOW_CATALOG_POOL_TABS);
